// 4 ago 2026 (pedido de Augusto): identificar visualmente cada cuenta por
// el banco o la red de tarjeta (Visa/Mastercard/Amex/...) a la que
// pertenece, en vez de solo texto. Se auto-detecta a partir de lo que el
// usuario ya escribe en "Entidad" y "Nombre de cuenta" (ver
// bancosSugeridos en CuentasPage.js) -- no requiere que el usuario elija
// nada a mano ni un campo nuevo en la base de datos.
//
// Dos fuentes de ícono, en este orden de prioridad:
//   1. Red de tarjeta (Visa/Mastercard/Amex/Diners/Discover): íconos
//      reales de marca vía react-icons/si (simple-icons), la misma
//      librería que ya usa PaymentMethodsBadges.js para PayPal/Mercado
//      Pago -- son glifos de UN SOLO COLOR (así los distribuye
//      simple-icons), no el arte oficial multicolor completo.
//   2. Banco (BCP, BBVA, Interbank, Scotiabank, ...): NO existen íconos de
//      marca reales para bancos peruanos en ninguna librería de íconos de
//      código abierto -- en vez de bajar y empaquetar el logo oficial de
//      cada banco (temas de licencia/marca registrada que Augusto tendría
//      que resolver directamente con cada banco), se arma una "insignia"
//      con las iniciales del banco sobre su color de marca aproximado.
//      Es el mismo patrón que usan apps como Slack o Gmail para
//      contactos sin foto. Los colores son aproximados de memoria --
//      ajústalos en BANCOS abajo si no calzan con el tono real.
// Si no calza nada de lo anterior, cae a un ícono genérico de Material UI
// según el tipo de cuenta (ahorros/corriente/efectivo/tarjeta/préstamo).

import { SiVisa, SiMastercard, SiAmericanexpress, SiDinersclub, SiDiscover } from 'react-icons/si';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import SavingsIcon from '@mui/icons-material/Savings';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import HomeIcon from '@mui/icons-material/Home';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import SchoolIcon from '@mui/icons-material/School';
import BusinessIcon from '@mui/icons-material/Business';

const normalizar = (texto) =>
  (texto || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');

// Redes de tarjeta -- orden importa: "american express" antes que
// cualquier cosa más corta que pudiera confundirse.
const REDES_TARJETA = [
  { claves: ['visa'], Icon: SiVisa, color: '#1A1F71', nombre: 'Visa' },
  { claves: ['mastercard', 'master card'], Icon: SiMastercard, color: '#EB001B', nombre: 'Mastercard' },
  { claves: ['american express', 'amex'], Icon: SiAmericanexpress, color: '#016FD0', nombre: 'American Express' },
  { claves: ['diners club', 'diners'], Icon: SiDinersclub, color: '#0079BE', nombre: 'Diners Club' },
  { claves: ['discover'], Icon: SiDiscover, color: '#FF6000', nombre: 'Discover' },
];

// Bancos con insignia de iniciales + color de marca aproximado. Cubre los
// bancos peruanos (donde operan los clientes reales de Darwin Optima) más
// un puñado de bancos globales frecuentes en bancosSugeridos
// (CuentasPage.js) -- el resto de esa lista cae al ícono genérico por
// tipo de cuenta, no todos necesitan insignia propia.
const BANCOS = [
  { claves: ['banco de credito', 'bcp'], iniciales: 'BCP', color: '#003DA5', nombre: 'BCP' },
  { claves: ['bbva'], iniciales: 'BBVA', color: '#072146', nombre: 'BBVA' },
  { claves: ['interbank'], iniciales: 'IBK', color: '#00A650', nombre: 'Interbank' },
  { claves: ['scotiabank'], iniciales: 'SCB', color: '#EC111A', nombre: 'Scotiabank' },
  { claves: ['banco de la nacion'], iniciales: 'BN', color: '#D52B1E', nombre: 'Banco de la Nación' },
  { claves: ['mibanco'], iniciales: 'MB', color: '#FDB913', nombre: 'MiBanco' },
  { claves: ['pichincha'], iniciales: 'BP', color: '#FFCC00', nombre: 'Banco Pichincha' },
  { claves: ['falabella'], iniciales: 'BF', color: '#75B72A', nombre: 'Banco Falabella' },
  { claves: ['ripley'], iniciales: 'BR', color: '#E2001A', nombre: 'Banco Ripley' },
  { claves: ['banbif', 'bif'], iniciales: 'BIF', color: '#8DC63F', nombre: 'BanBif' },
  { claves: ['santander'], iniciales: 'SAN', color: '#EC0000', nombre: 'Santander' },
  { claves: ['hsbc'], iniciales: 'HSBC', color: '#DB0011', nombre: 'HSBC' },
  { claves: ['citibank', 'citibanamex', 'citi'], iniciales: 'CITI', color: '#003A79', nombre: 'Citibank' },
  { claves: ['jpmorgan', 'chase'], iniciales: 'CHASE', color: '#117ACA', nombre: 'Chase' },
  { claves: ['bank of america'], iniciales: 'BOA', color: '#012169', nombre: 'Bank of America' },
];

// Fallback por tipo de cuenta (ver TIPOS_ACTIVO/TIPOS_DEUDA en
// CuentasPage.js) cuando no se reconoce ni red de tarjeta ni banco --
// ej. efectivo, o un banco que no está en la lista de arriba.
const ICONO_POR_TIPO = {
  efectivo: { Icon: AccountBalanceWalletIcon, color: '#2E7D32' },
  ahorros: { Icon: SavingsIcon, color: '#1565C0' },
  corriente: { Icon: AccountBalanceIcon, color: '#455A64' },
  tarjeta_credito: { Icon: CreditCardIcon, color: '#6A1B9A' },
  prestamo_personal: { Icon: AccountBalanceIcon, color: '#8D6E63' },
  prestamo_hipotecario: { Icon: HomeIcon, color: '#5D4037' },
  prestamo_auto: { Icon: DirectionsCarIcon, color: '#00838F' },
  prestamo_educacion: { Icon: SchoolIcon, color: '#F9A825' },
  prestamo_proveedores: { Icon: BusinessIcon, color: '#37474F' },
};

// Busca coincidencia de red de tarjeta en cualquiera de los textos dados
// (nombre de cuenta + entidad, porque el usuario puede escribir "Visa" en
// cualquiera de los dos campos).
export const getCardNetwork = (...textos) => {
  const norm = normalizar(textos.join(' '));
  return REDES_TARJETA.find((r) => r.claves.some((c) => norm.includes(c))) || null;
};

export const getBankBadge = (entidad) => {
  const norm = normalizar(entidad);
  if (!norm) return null;
  return BANCOS.find((b) => b.claves.some((c) => norm.includes(c))) || null;
};

// Resuelve qué mostrar para una cuenta: { kind: 'red'|'banco'|'tipo', ... }
// -- kind le dice al componente que la use si debe renderizar un ícono de
// react-icons (red de tarjeta), una insignia de texto (banco) o un ícono
// de Material UI (fallback por tipo).
export const getAccountIcon = ({ nombre, entidad, tipo } = {}) => {
  const red = getCardNetwork(nombre, entidad);
  if (red) return { kind: 'red', ...red };

  const banco = getBankBadge(entidad);
  if (banco) return { kind: 'banco', ...banco };

  const porTipo = ICONO_POR_TIPO[tipo] || { Icon: AccountBalanceWalletIcon, color: '#757575' };
  return { kind: 'tipo', ...porTipo };
};

export default getAccountIcon;
